import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../base/ConfirmationDialog";
import SinglePage from "../SinglePage/SinglePage";
import { AUTH_CHANGED_EVENT, loadAuthSession } from "../../../services/authStorage";
import {
  getShopReservationsForManager,
  ShopReservationDto,
  ShopReservationStatus,
  updateShopReservationAsManager,
} from "../../../services/shopApi";
import { downloadInvoiceDocument } from "../Shop/invoicePrint";
import { COLORS } from "../../../values/colors";
import { formatGermanDate, formatGermanDateTime, formatGermanDateTimeForExport, getGermanDateKey } from "../../../utils/dateTime";
import {
  ActionRow,
  Button,
  ContentContainer,
  Eyebrow,
  Field,
  FieldLabel,
  FormGrid,
  HeroBanner,
  HeroText,
  HeroTitle,
  Input,
  ItemCard,
  ItemList,
  ItemMeta,
  ItemTitle,
  ItemTitleRow,
  Message,
  NoticeCard,
  NoticeText,
  NoticeTitle,
  ReservationCard,
  ReservationHeader,
  ReservationSubline,
  ReservationTitle,
  Select,
  StatusBadge,
} from "./styles";

type EditableReservationItem = ShopReservationDto["items"][number] & {
  unitPriceDraft: string;
};

type EditableReservation = Omit<ShopReservationDto, "items"> & {
  items: EditableReservationItem[];
  managerNoteDraft: string;
  message: string;
  tone?: "error" | "success";
};

type InvoiceDraftItem = {
  id: string;
  productName: string;
  quantity: number;
  articleNumbersSummary: string;
  selectedOptionsSummary: string;
  unitPriceDraft: string;
};

type InvoiceDraftState = {
  reservationId: string;
  reservationShortId: string;
  invoiceNumberDraft: string;
  transferIbanDraft: string;
  createdAtUtc: string;
  customerName: string;
  customerEmail: string;
  kleidlesnummer: string;
  managerNote: string;
  items: InvoiceDraftItem[];
};

type ReservationSortMode =
  | "newest"
  | "oldest"
  | "name-asc"
  | "name-desc"
  | "status-asc"
  | "status-desc";

type ExportStatusFilter = "alle" | ShopReservationStatus;

const STATUS_LABELS: Record<ShopReservationStatus, string> = {
  offen: "Offen",
  bestaetigt: "Bestaetigt",
  erledigt: "Erledigt",
  storniert: "Storniert",
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const formatDateForExcelExport = (value: string) => `="${formatGermanDateTimeForExport(value)}"`;

const escapeCsvValue = (value: string | number) => {
  const normalized = String(value).replace(/"/g, '""');
  return `"${normalized}"`;
};

const formatPriceDraft = (value: number) => value.toFixed(2).replace(".", ",");

const getArticleNumbersDisplay = (articleNumbersSummary?: string) =>
  articleNumbersSummary && articleNumbersSummary.trim()
    ? articleNumbersSummary
    : "Keine Artikelnummer hinterlegt";

const parsePriceDraft = (value: string): number | null => {
  const normalized = value.trim().replace(/\s+/g, "").replace(",", ".");

  if (!normalized || !/^\d+(\.\d{0,2})?$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const toEditableReservation = (reservation: ShopReservationDto): EditableReservation => ({
  ...reservation,
  items: reservation.items.map((item) => ({
    ...item,
    unitPriceDraft: formatPriceDraft(item.unitPrice),
  })),
  managerNoteDraft: reservation.managerNote ?? "",
  message: "",
});

const getReservationTotal = (reservation: EditableReservation) =>
  reservation.items.reduce((sum, item) => {
    const price = parsePriceDraft(item.unitPriceDraft) ?? item.unitPrice;
    return sum + price * item.quantity;
  }, 0);

const getInvoiceDraftTotal = (invoiceDraft: InvoiceDraftState) =>
  invoiceDraft.items.reduce((sum, item) => {
    const price = parsePriceDraft(item.unitPriceDraft) ?? 0;
    return sum + price * item.quantity;
  }, 0);

const validateReservationItems = (reservation: EditableReservation) => {
  const items = reservation.items.map((item) => {
    const unitPrice = parsePriceDraft(item.unitPriceDraft);
    if (unitPrice === null) {
      throw new Error(`Der Preis für '${item.productName}' ist ungültig.`);
    }

    return {
      id: item.id,
      unitPrice,
    };
  });

  return items;
};

const validateInvoiceDraft = (invoiceDraft: InvoiceDraftState) =>
  invoiceDraft.items.map((item) => {
    const unitPrice = parsePriceDraft(item.unitPriceDraft);
    if (unitPrice === null) {
      throw new Error(`Der Rechnungspreis für '${item.productName}' ist ungültig.`);
    }

    return {
      ...item,
      unitPrice,
    };
  });

const buildInvoiceDraft = (reservation: EditableReservation): InvoiceDraftState => ({
  reservationId: reservation.id,
  reservationShortId: reservation.id.slice(0, 8),
  invoiceNumberDraft: `RES-${reservation.id.slice(0, 8).toUpperCase()}`,
  transferIbanDraft: "",
  createdAtUtc: reservation.createdAtUtc,
  customerName: `${reservation.user.vorname} ${reservation.user.name}`,
  customerEmail: reservation.user.email,
  kleidlesnummer: reservation.user.kleidlesnummer,
  managerNote: reservation.managerNoteDraft.trim(),
  items: reservation.items.map((item) => ({
    id: item.id,
    productName: item.productName,
    quantity: item.quantity,
    articleNumbersSummary: item.articleNumbersSummary,
    selectedOptionsSummary: item.selectedOptionsSummary,
    unitPriceDraft: item.unitPriceDraft,
  })),
});

const ShopReservationsManagement = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(loadAuthSession());
  const [reservations, setReservations] = useState<EditableReservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] = useState<ReservationSortMode>("newest");
  const [exportSearchTerm, setExportSearchTerm] = useState("");
  const [exportStatusFilter, setExportStatusFilter] = useState<ExportStatusFilter>("alle");
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportError, setExportError] = useState("");
  const [invoiceDraft, setInvoiceDraft] = useState<InvoiceDraftState | null>(null);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);

  useEffect(() => {
    const sync = () => setSession(loadAuthSession());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, sync);
  }, []);

  const token = session?.token ?? "";
  const isShopManager = session?.user.role === "shopmanager";

  useEffect(() => {
    if (!session) {
      navigate("/konto", { replace: true });
      return;
    }

    if (!isShopManager) {
      navigate("/", { replace: true });
      return;
    }

    const loadReservations = async () => {
      try {
        const result = await getShopReservationsForManager(token);
        setReservations(result.map(toEditableReservation));
        setLoadError("");
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : "Reservierungen konnten nicht geladen werden.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadReservations();
  }, [isShopManager, navigate, session, token]);

  const visibleReservations = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredReservations = reservations.filter((reservation) => {
      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        reservation.id,
        reservation.status,
        reservation.managerNoteDraft,
        reservation.user.vorname,
        reservation.user.name,
        reservation.user.email,
        reservation.user.kleidlesnummer,
        ...reservation.items.flatMap((item) => [
          item.productName,
          item.productSlug,
          item.selectedOptionsSummary,
          getArticleNumbersDisplay(item.articleNumbersSummary),
        ]),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });

    return [...filteredReservations].sort((left, right) => {
      if (sortMode === "oldest") {
        return new Date(left.createdAtUtc).getTime() - new Date(right.createdAtUtc).getTime();
      }

      if (sortMode === "name-asc") {
        return `${left.user.vorname} ${left.user.name}`.localeCompare(
          `${right.user.vorname} ${right.user.name}`,
          "de-DE"
        );
      }

      if (sortMode === "name-desc") {
        return `${right.user.vorname} ${right.user.name}`.localeCompare(
          `${left.user.vorname} ${left.user.name}`,
          "de-DE"
        );
      }

      if (sortMode === "status-asc") {
        return STATUS_LABELS[left.status].localeCompare(STATUS_LABELS[right.status], "de-DE");
      }

      if (sortMode === "status-desc") {
        return STATUS_LABELS[right.status].localeCompare(STATUS_LABELS[left.status], "de-DE");
      }

      return new Date(right.createdAtUtc).getTime() - new Date(left.createdAtUtc).getTime();
    });
  }, [reservations, searchTerm, sortMode]);

  const exportableReservations = useMemo(() => {
    const normalizedSearch = exportSearchTerm.trim().toLowerCase();

    return reservations.filter((reservation) => {
      if (exportStatusFilter !== "alle" && reservation.status !== exportStatusFilter) {
        return false;
      }

      if (exportDateFrom) {
        const createdAtKey = getGermanDateKey(reservation.createdAtUtc);
        if (createdAtKey < exportDateFrom) {
          return false;
        }
      }

      if (exportDateTo) {
        const createdAtKey = getGermanDateKey(reservation.createdAtUtc);
        if (createdAtKey > exportDateTo) {
          return false;
        }
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        reservation.id,
        reservation.status,
        reservation.managerNoteDraft,
        reservation.user.vorname,
        reservation.user.name,
        reservation.user.email,
        reservation.user.kleidlesnummer,
        ...reservation.items.flatMap((item) => [
          item.productName,
          item.productSlug,
          item.selectedOptionsSummary,
          getArticleNumbersDisplay(item.articleNumbersSummary),
        ]),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [exportDateFrom, exportDateTo, exportSearchTerm, exportStatusFilter, reservations]);

  const updateLocalReservation = (
    reservationId: string,
    updater: (current: EditableReservation) => EditableReservation
  ) => {
    setReservations((prev) =>
      prev.map((reservation) => (reservation.id === reservationId ? updater(reservation) : reservation))
    );
  };

  const handleSave = async (reservation: EditableReservation) => {
    try {
      const items = validateReservationItems(reservation);
      const updated = await updateShopReservationAsManager(token, reservation.id, {
        status: reservation.status,
        managerNote: reservation.managerNoteDraft.trim() || undefined,
        items,
      });

      updateLocalReservation(reservation.id, () => ({
        ...toEditableReservation(updated),
        message: "Reservierung gespeichert.",
        tone: "success",
      }));
    } catch (error) {
      updateLocalReservation(reservation.id, (current) => ({
        ...current,
        message: error instanceof Error ? error.message : "Reservierung konnte nicht gespeichert werden.",
        tone: "error",
      }));
    }
  };

  const handleOpenInvoiceDialog = (reservation: EditableReservation) => {
    try {
      validateReservationItems(reservation);
      setInvoiceDraft(buildInvoiceDraft(reservation));
    } catch (error) {
      updateLocalReservation(reservation.id, (current) => ({
        ...current,
        message: error instanceof Error ? error.message : "Rechnung konnte nicht vorbereitet werden.",
        tone: "error",
      }));
    }
  };

  const handleInvoiceNumberChange = (value: string) => {
    setInvoiceDraft((current) => (current ? { ...current, invoiceNumberDraft: value } : current));
  };

  const handleInvoiceTransferIbanChange = (value: string) => {
    setInvoiceDraft((current) => (current ? { ...current, transferIbanDraft: value } : current));
  };

  const handleInvoiceItemPriceChange = (itemId: string, value: string) => {
    setInvoiceDraft((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        items: current.items.map((item) =>
          item.id === itemId
            ? {
                ...item,
                unitPriceDraft: value,
              }
            : item
        ),
      };
    });
  };

  const handleCloseInvoiceDialog = () => {
    setInvoiceDraft(null);
  };

  const handleDownloadInvoice = async () => {
    if (!invoiceDraft) {
      return;
    }

    try {
      const invoiceNumber = invoiceDraft.invoiceNumberDraft.trim();
      if (!invoiceNumber) {
        throw new Error("Bitte gib eine Rechnungsnummer an.");
      }

      const transferIban = invoiceDraft.transferIbanDraft.trim();

      setIsDownloadingInvoice(true);
      const validatedItems = validateInvoiceDraft(invoiceDraft);
      const downloaded = await downloadInvoiceDocument({
        title: "Rechnung Shop-Reservierung",
        subtitle: "Erstellt aus der Reservierung.",
        invoiceNumber,
        dateLabel: formatGermanDate(new Date().toISOString()),
        referenceLabel: `Reservierung ${invoiceDraft.reservationShortId} vom ${formatGermanDate(invoiceDraft.createdAtUtc)}`,
        customer: {
          name: invoiceDraft.customerName,
          email: invoiceDraft.customerEmail,
          kleidlesnummer: invoiceDraft.kleidlesnummer,
        },
        paymentIban: transferIban || undefined,
        entries: validatedItems.map((item) => ({
          productName: item.productName,
          articleNumbersSummary: getArticleNumbersDisplay(item.articleNumbersSummary),
          price: item.unitPrice,
          quantity: item.quantity,
          selectedOptionsSummary: item.selectedOptionsSummary,
        })),
        footerHint: invoiceDraft.managerNote
          ? `Interne Notiz: ${invoiceDraft.managerNote}`
          : undefined,
        fileName: `rechnung-${invoiceNumber.toLowerCase().replace(/[^a-z0-9-]+/g, "-")}.pdf`,
      });

      updateLocalReservation(invoiceDraft.reservationId, (current) => ({
        ...current,
        message: downloaded
          ? "Rechnung wurde heruntergeladen."
          : "Rechnung konnte nicht heruntergeladen werden.",
        tone: downloaded ? "success" : "error",
      }));

      if (downloaded) {
        setInvoiceDraft(null);
      }
    } catch (error) {
      updateLocalReservation(invoiceDraft.reservationId, (current) => ({
        ...current,
        message: error instanceof Error ? error.message : "Rechnung konnte nicht erstellt werden.",
        tone: "error",
      }));
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  const handleExport = () => {
    setExportError("");

    if (exportDateFrom && exportDateTo && exportDateFrom > exportDateTo) {
      setExportError("Das Startdatum darf nicht nach dem Enddatum liegen.");
      return;
    }

    if (exportableReservations.length === 0) {
      setExportError("Mit den aktuellen Exportfiltern wurden keine Reservierungen gefunden.");
      return;
    }

    const header = [
      "Reservierung-ID",
      "Status",
      "Erstellt am",
      "Aktualisiert am",
      "Vorname",
      "Nachname",
      "E-Mail",
      "Kleidlesnummer",
      "Produkt",
      "Produktslug",
      "Produktnummern",
      "Menge",
      "Einzelpreis",
      "Gesamtpreis",
      "Optionen",
      "Managernotiz",
    ];

    const rows = exportableReservations.flatMap((reservation) =>
      reservation.items.map((item) => {
        const unitPrice = parsePriceDraft(item.unitPriceDraft) ?? item.unitPrice;
        return [
          reservation.id,
          STATUS_LABELS[reservation.status],
          formatDateForExcelExport(reservation.createdAtUtc),
          formatDateForExcelExport(reservation.updatedAtUtc),
          reservation.user.vorname,
          reservation.user.name,
          reservation.user.email,
          reservation.user.kleidlesnummer,
          item.productName,
          item.productSlug,
          getArticleNumbersDisplay(item.articleNumbersSummary),
          item.quantity,
          unitPrice.toFixed(2),
          (unitPrice * item.quantity).toFixed(2),
          item.selectedOptionsSummary,
          reservation.managerNoteDraft,
        ];
      })
    );

    const csvContent = [header, ...rows]
      .map((row) => row.map((value) => escapeCsvValue(value)).join(";"))
      .join("\n");

    const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `reservierungen-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <SinglePage
        title="Reservierungen verwalten"
        heroBanner={
          <HeroBanner>
            <Eyebrow>Shopverwaltung</Eyebrow>
            <HeroTitle>Reservierungen verwalten.</HeroTitle>
            <HeroText>
              Hier werden alle unverbindlichen Shop-Reservierungen geprüft, bestätigt, abgeschlossen oder
              storniert. Dieser Bereich ist ausschließlich für Shopmanager gedacht.
            </HeroText>
          </HeroBanner>
        }
        content={
          <ContentContainer>
            <NoticeCard>
              <NoticeTitle>Reservierungsübersicht</NoticeTitle>
              <NoticeText>
                {isLoading
                  ? "Reservierungen werden geladen."
                  : loadError
                    ? loadError
                    : `${visibleReservations.length} Reservierungen sichtbar.`}
              </NoticeText>
            </NoticeCard>

            <NoticeCard>
              <FormGrid>
                <Field>
                  <FieldLabel>Suche</FieldLabel>
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Name, E-Mail, Kleidlesnummer, Artikel oder Status"
                  />
                </Field>
                <Field>
                  <FieldLabel>Sortierung</FieldLabel>
                  <Select
                    value={sortMode}
                    onChange={(event) => setSortMode(event.target.value as ReservationSortMode)}
                  >
                    <option value="newest">Neueste zuerst</option>
                    <option value="oldest">Älteste zuerst</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="status-asc">Status A-Z</option>
                    <option value="status-desc">Status Z-A</option>
                  </Select>
                </Field>
              </FormGrid>
            </NoticeCard>

            <NoticeCard>
              <NoticeTitle>Export</NoticeTitle>
              <NoticeText>
                Exportiere die Reservierungsliste als CSV. Die Exportfilter gelten unabhaengig von der
                aktuellen Bildschirmansicht.
              </NoticeText>
              <FormGrid style={{ marginTop: "1rem" }}>
                <Field>
                  <FieldLabel>Suche im Export</FieldLabel>
                  <Input
                    value={exportSearchTerm}
                    onChange={(event) => setExportSearchTerm(event.target.value)}
                    placeholder="Name, E-Mail, Artikel oder Status"
                  />
                </Field>
                <Field>
                  <FieldLabel>Status</FieldLabel>
                  <Select
                    value={exportStatusFilter}
                    onChange={(event) => setExportStatusFilter(event.target.value as ExportStatusFilter)}
                  >
                    <option value="alle">Alle</option>
                    <option value="offen">Offen</option>
                    <option value="bestaetigt">Bestaetigt</option>
                    <option value="erledigt">Erledigt</option>
                    <option value="storniert">Storniert</option>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel>Datum von</FieldLabel>
                  <Input
                    type="date"
                    value={exportDateFrom}
                    onChange={(event) => setExportDateFrom(event.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel>Datum bis</FieldLabel>
                  <Input
                    type="date"
                    value={exportDateTo}
                    onChange={(event) => setExportDateTo(event.target.value)}
                  />
                </Field>
              </FormGrid>
              <NoticeText style={{ marginTop: "1rem" }}>
                {exportableReservations.length} Reservierungen entsprechen aktuell den Exportfiltern.
              </NoticeText>
              {exportError ? <Message $tone="error">{exportError}</Message> : null}
              <ActionRow style={{ marginTop: "1rem" }}>
                <Button type="button" $variant="primary" onClick={handleExport}>
                  CSV exportieren
                </Button>
              </ActionRow>
            </NoticeCard>

            {!isLoading && !loadError
              ? visibleReservations.map((reservation) => {
                  const total = getReservationTotal(reservation);

                  return (
                    <ReservationCard key={reservation.id}>
                      <ReservationHeader>
                        <div>
                          <ReservationTitle>
                            {reservation.user.vorname} {reservation.user.name}
                          </ReservationTitle>
                          <ReservationSubline>
                            Reservierung {reservation.id.slice(0, 8)} | {reservation.user.email} | Kleidlesnummer{" "}
                            {reservation.user.kleidlesnummer}
                          </ReservationSubline>
                          <ReservationSubline>
                            Eingegangen am {formatGermanDateTime(reservation.createdAtUtc)} | Letzte Aenderung{" "}
                            {formatGermanDateTime(reservation.updatedAtUtc)}
                          </ReservationSubline>
                        </div>
                        <StatusBadge>{STATUS_LABELS[reservation.status]}</StatusBadge>
                      </ReservationHeader>

                      <ItemList>
                        {reservation.items.map((item) => {
                          const parsedUnitPrice = parsePriceDraft(item.unitPriceDraft);
                          const lineTotal = (parsedUnitPrice ?? item.unitPrice) * item.quantity;

                          return (
                            <ItemCard key={item.id}>
                              <ItemTitleRow>
                                <ItemTitle>
                                  {item.productName} x {item.quantity}
                                </ItemTitle>
                                <ItemMeta>{formatCurrency(lineTotal)}</ItemMeta>
                              </ItemTitleRow>
                              <ItemMeta>{item.selectedOptionsSummary}</ItemMeta>
                              <ItemMeta>
                                Produktnummern {getArticleNumbersDisplay(item.articleNumbersSummary)} | Artikel {item.productSlug}
                              </ItemMeta>
                              <FormGrid style={{ marginTop: "0.9rem" }}>
                                <Field>
                                  <FieldLabel>Einzelpreis in EUR</FieldLabel>
                                  <Input
                                    value={item.unitPriceDraft}
                                    onChange={(event) =>
                                      updateLocalReservation(reservation.id, (current) => ({
                                        ...current,
                                        message: "",
                                        items: current.items.map((currentItem) =>
                                          currentItem.id === item.id
                                            ? { ...currentItem, unitPriceDraft: event.target.value }
                                            : currentItem
                                        ),
                                      }))
                                    }
                                    placeholder="z.B. 29,00"
                                  />
                                </Field>
                                <Field>
                                  <FieldLabel>Positionssumme</FieldLabel>
                                  <Input value={formatCurrency(lineTotal)} readOnly />
                                </Field>
                              </FormGrid>
                            </ItemCard>
                          );
                        })}
                      </ItemList>

                      <FormGrid>
                        <Field>
                          <FieldLabel>Status</FieldLabel>
                          <Select
                            value={reservation.status}
                            onChange={(event) =>
                              updateLocalReservation(reservation.id, (current) => ({
                                ...current,
                                status: event.target.value as ShopReservationStatus,
                                message: "",
                              }))
                            }
                          >
                            <option value="offen">Offen</option>
                            <option value="bestaetigt">Bestaetigt</option>
                            <option value="erledigt">Erledigt</option>
                            <option value="storniert">Storniert</option>
                          </Select>
                        </Field>
                        <Field>
                          <FieldLabel>Gesamtsumme</FieldLabel>
                          <Input value={formatCurrency(total)} readOnly />
                        </Field>
                        <Field style={{ gridColumn: "1 / -1" }}>
                          <FieldLabel>Interne Notiz</FieldLabel>
                          <Input
                            value={reservation.managerNoteDraft}
                            onChange={(event) =>
                              updateLocalReservation(reservation.id, (current) => ({
                                ...current,
                                managerNoteDraft: event.target.value,
                                message: "",
                              }))
                            }
                            placeholder="Optionaler Hinweis für die Shopverwaltung"
                          />
                        </Field>
                      </FormGrid>

                      {reservation.message ? <Message $tone={reservation.tone}>{reservation.message}</Message> : null}

                      <ActionRow>
                        <Button type="button" $variant="ghost" onClick={() => handleOpenInvoiceDialog(reservation)}>
                          Rechnung generieren
                        </Button>
                        <Button type="button" $variant="primary" onClick={() => void handleSave(reservation)}>
                          Reservierung speichern
                        </Button>
                      </ActionRow>
                    </ReservationCard>
                  );
                })
              : null}
          </ContentContainer>
        }
      />

      <ConfirmationDialog
        open={invoiceDraft !== null}
        title="Rechnung vorbereiten"
        description={
          invoiceDraft ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ display: "grid", gap: 1.2, color: COLORS.textSecondary }}>
                <Field style={{ gap: "0.45rem" }}>
                  <FieldLabel>Rechnungsnummer</FieldLabel>
                  <Input
                    value={invoiceDraft.invoiceNumberDraft}
                    onChange={(event) => handleInvoiceNumberChange(event.target.value)}
                    placeholder="z.B. RES-AB12CD34"
                  />
                </Field>
                <Field style={{ gap: "0.45rem" }}>
                  <FieldLabel>IBAN für Überweisung</FieldLabel>
                  <Input
                    value={invoiceDraft.transferIbanDraft}
                    onChange={(event) => handleInvoiceTransferIbanChange(event.target.value)}
                    placeholder="z.B. DE12 3456 7890 1234 5678 90"
                  />
                </Field>
                <div>
                  <strong style={{ color: COLORS.textPrimary }}>Empfänger:</strong> {invoiceDraft.customerName}
                </div>
                <div>{invoiceDraft.customerEmail}</div>
                <div>Kleidlesnummer: {invoiceDraft.kleidlesnummer}</div>
                <div>Gesamtbetrag: {formatCurrency(getInvoiceDraftTotal(invoiceDraft))}</div>
              </Box>

              <Box sx={{ display: "grid", gap: 1.25 }}>
                {invoiceDraft.items.map((item) => {
                  const parsedPrice = parsePriceDraft(item.unitPriceDraft);
                  const lineTotal = (parsedPrice ?? 0) * item.quantity;

                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "grid",
                        gap: 1,
                        padding: "0.95rem 1rem",
                        borderRadius: "0.9rem",
                        border: `1px solid ${COLORS.gray}`,
                        backgroundColor: "rgba(255,255,255,0.03)",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                        <strong style={{ color: COLORS.textPrimary }}>
                          {item.productName} x {item.quantity}
                        </strong>
                        <span style={{ color: COLORS.textSecondary }}>{formatCurrency(lineTotal)}</span>
                      </Box>
                      <div style={{ color: COLORS.textSecondary }}>{item.selectedOptionsSummary}</div>
                      <div style={{ color: COLORS.textSecondary }}>Produktnummern {getArticleNumbersDisplay(item.articleNumbersSummary)}</div>
                      <Input
                        value={item.unitPriceDraft}
                        onChange={(event) => handleInvoiceItemPriceChange(item.id, event.target.value)}
                        placeholder="Rechnungspreis in EUR"
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null
        }
        confirmText="PDF herunterladen"
        cancelText="Abbrechen"
        isConfirming={isDownloadingInvoice}
        maxWidth="md"
        onConfirm={() => { void handleDownloadInvoice(); }}
        onClose={handleCloseInvoiceDialog}
      />
    </>
  );
};

export default ShopReservationsManagement;
